import { EffectFade, Thumbs } from 'swiper/modules';
import Swiper from 'swiper';

interface GtmProduct {
  sku: string;
  gtin: string;
  stock: number;
  price: number;
}

export default function initProductDetails() {
  Alpine.data('productDetails', () => ({
    selectedRegion: '',
    selectedVariant: 0,
    salePrice: '',
    oldPrice: '',
    discountPercentage: '',
    gtmProduct: null as GtmProduct | null,
    productGallerySwiper: null as Swiper | null,
    embed: '',
    videoWrapper: null as HTMLElement | null,
    successDialogOpened: false,
    submitting: true,
    showBackInStock: false,
    showBackInStockThankYou: false,
    bisSubmitting: false,
    init() {
      const thumbEl = document.querySelector('.product-gallery-thumbnail-swiper') as HTMLElement;
      const swiperEl = document.querySelector('.product-gallery-swiper') as HTMLElement;
      if (thumbEl && swiperEl) {
        const thumbnailSwiper = new Swiper(thumbEl, {
          loop: true,
          spaceBetween: 30,
          slidesPerView: 'auto',
          direction: 'vertical',
          freeMode: true,
          watchSlidesProgress: true,
        });
        this.productGallerySwiper = new Swiper(swiperEl, {
          modules: [EffectFade, Thumbs],
          effect: 'fade',
          loop: true,
          slidesPerView: 1,
          thumbs: {
            swiper: thumbnailSwiper,
          },
        });
      }

      const productColorEl = this.$el.querySelector('.product-color-swiper') as HTMLElement;
      if (productColorEl) {
        // eslint-disable-next-line no-new
        new Swiper(productColorEl, {
          spaceBetween: 8,
          slidesPerView: 'auto',
        });
      }

      const mobileProductGalleryEl = this.$el.querySelector('.mobile-product-gallery-swiper') as HTMLElement;
      if (mobileProductGalleryEl) {
        // eslint-disable-next-line no-new
        new Swiper(mobileProductGalleryEl, {
          spaceBetween: 12,
          slidesPerView: 'auto',
        });
      }

      this.videoWrapper = this.$el.querySelector('.product-video-wrapper .aspect-video') as HTMLVideoElement;
      this.embed = this.videoWrapper?.firstElementChild?.outerHTML ?? '';
    },
    selectVariant(id: number) {
      this.showBackInStock = false;
      this.showBackInStockThankYou = false;
      this.selectedVariant = id;
      const variant = document.getElementById(`variant-${id}`);
      if (variant) {
        const {
          price,
          oldPrice,
          discountPercentage,
          sku,
          ean,
          finalPrice,
          stock,
        } = variant.dataset;
        this.salePrice = price ?? this.salePrice;
        this.oldPrice = oldPrice ?? '';
        this.discountPercentage = discountPercentage ?? '';
        this.gtmProduct = {
          sku: sku ?? '',
          gtin: ean ?? '',
          stock: parseInt(stock ?? '0', 10),
          price: parseFloat(finalPrice ?? '0'),
        };
      }
      const input = document.getElementById('purchasableId') as HTMLInputElement;
      if (input) {
        input.value = `${id}`;
      }
    },
    isVariantSelected(id: number) {
      return this.selectedVariant === id;
    },
    selectRegion(key: string) {
      this.selectedRegion = key;
    },
    isRegionSelected(key: string) {
      return this.selectedRegion === key;
    },
    openGallery(slideIndex: number) {
      this.productGallerySwiper?.slideTo(slideIndex);
      const dialog = document.getElementById('product-gallery-dialog');
      if (dialog) {
        // @ts-ignore
        dialog.show();
      }
    },
    openVideoDialog() {
      const dialog = document.getElementById('video-dialog');
      if (dialog && this.videoWrapper && this.embed) {
        const wrap = document.createElement('div');
        wrap.innerHTML = this.embed;
        const iframe = wrap.firstElementChild;
        if (iframe) {
          iframe.removeAttribute('width');
          iframe.removeAttribute('height');
          iframe.className = 'w-full h-full';
          const src = iframe.getAttribute('src') ?? '';
          iframe.setAttribute('src', `${src}${src.indexOf('?') > -1 ? '&' : '?'}autoplay=1&rel=0`);
          iframe.toggleAttribute('autoplay', true);
          this.videoWrapper.innerHTML = iframe.outerHTML;
          // @ts-ignore
          dialog.show();
        }
      }
    },
    closeVideoDialog() {
      document.body.classList.remove('dialog-active');
      if (this.videoWrapper) {
        this.videoWrapper.innerHTML = '';
      }
      const dialog = document.getElementById('video-dialog');
      if (dialog) {
        // @ts-ignore
        dialog.close();
      }
    },
    handleSubmit(event: FormDataEvent) {
      if (this.submitting) {
        return;
      }
      this.submitting = true;

      const form = event.target as HTMLFormElement;
      fetch('/', {
        method: 'POST',
        body: new FormData(form),
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        redirect: 'manual',
      })
        .then(() => {
          this.submitting = false;
          // @ts-ignore
          htmx.trigger('#account-menu', 'refresh');
          // @ts-ignore
          htmx.trigger('#account-menu-mobile', 'refresh');
          this.openSuccessDialog();
        })
        .catch(() => {
          this.submitting = false;
        });
    },
    handleBackInStockSubmit(event: FormDataEvent) {
      if (this.bisSubmitting) {
        return;
      }
      this.bisSubmitting = true;

      const form = event.target as HTMLFormElement;
      fetch('/', {
        method: 'POST',
        body: new FormData(form),
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        redirect: 'manual',
      })
        .then(() => {
          this.bisSubmitting = false;
          this.showBackInStockThankYou = true;
        })
        .catch(() => {
          this.bisSubmitting = false;
        });
    },
    openSuccessDialog() {
      this.gtmProductAddToCartPushEvent();
      const dialog = document.getElementById('success-dialog');
      // @ts-ignore
      dialog.show();
    },
    showBackInStockSection(id: number) {
      this.showBackInStockThankYou = false;
      this.showBackInStock = true;
      this.selectedVariant = id;
    },
    gtmProductAddToCartPushEvent() {
      if (window.ccc && window.ccc['default-statistics'] && window.DATALAYER) {
        const ecommerceObj = {
          currencyCode: window.CRAFT_COMMERCE_CURRENCY,
          add: {
            products: [this.gtmProduct],
          },
        };

        window.DATALAYER.push({
          event: 'addToCart',
          ecommerce: ecommerceObj,
          eventTimeout: 2000,
        });
      }
    },
    initDefault(
      selectedRegion: string,
      defaultSalePrice: string,
      defaultOldPrice: string,
      defaultDiscountPercentage: string,
      defaultSku: string,
      defaultEan: string,
      selectedVariant: number,
      showBackInStock: boolean,
    ) {
      this.showBackInStock = showBackInStock;
      this.selectedVariant = selectedVariant;
      this.selectedRegion = selectedRegion;
      if (this.selectedVariant !== 0) {
        this.selectVariant(this.selectedVariant);
      } else {
        this.salePrice = defaultSalePrice;
        this.oldPrice = defaultOldPrice;
        this.discountPercentage = defaultDiscountPercentage;
      }

      const addToCartForm = document.getElementById('add-to-cart-form');
      if (addToCartForm) {
        const observer = new MutationObserver(() => {
          this.submitting = addToCartForm.querySelectorAll('[name="CRAFT_CSRF_TOKEN"]').length === 0;
        });

        // Configure observer: watch children for additions
        observer.observe(addToCartForm, { childList: true, subtree: true });
        this.submitting = addToCartForm.querySelectorAll('[name="CRAFT_CSRF_TOKEN"]').length === 0;
      }

      const backInStockForm = document.getElementById('back-in-stock-form');
      if (backInStockForm) {
        const observer = new MutationObserver(() => {
          this.bisSubmitting = backInStockForm.querySelectorAll('[name="CRAFT_CSRF_TOKEN"]').length === 0;
        });

        // Configure observer: watch children for additions
        observer.observe(backInStockForm, { childList: true, subtree: true });
        this.bisSubmitting = backInStockForm.querySelectorAll('[name="CRAFT_CSRF_TOKEN"]').length === 0;
      }
    },
  }));
}
